'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

/* ─── DATA ───────────────────────────────────────────────── */

const partnerTypes = [
  { icon: 'TruckIcon', label: 'Last-mile Delivery Networks', desc: 'High-volume delivery partners covering pan-India pin codes with rider networks.', examples: 'Shadowfax, Borzo, WeFast' },
  { icon: 'BoltIcon', label: 'Hyperlocal Express Couriers', desc: 'Same-hour and same-day delivery specialists for dense urban zones.', examples: 'Dunzo, Swiggy Genie, Porter' },
  { icon: 'BuildingStorefrontIcon', label: 'Long-haul Logistics Carriers', desc: 'B2B carriers for intercity and interstate shipment fulfilment.', examples: 'Delhivery, Ecom Express, XpressBees' },
  { icon: 'MapPinIcon', label: 'Regional & Local Networks', desc: 'State-specific and city-level delivery operators with strong local coverage.', examples: 'Regional operators by city' },
  { icon: 'UserGroupIcon', label: 'Own Fleet via Rider App', desc: 'Integrate your own managed riders as a native delivery partner alongside external ones.', examples: 'Your own drivers, via Movio Rider App' },
  { icon: 'ArrowsRightLeftIcon', label: 'Third-party Aggregators', desc: 'Aggregator platforms that give you access to multiple carriers through a single API.', examples: 'Shiprocket, Pickrr, Shipway' },
];

const coreFeatures = [
  {
    num: '01',
    icon: 'ArrowPathIcon',
    label: 'Third-party Delivery API Integrations',
    desc: 'Connect to any delivery partner — with pre-built connectors for major networks or a flexible custom API integration framework. New partners are onboarded in days, not months. Authentication, rate limiting, retries, and error handling are all managed by the middleware layer.',
    pills: ['Pre-built connectors', 'Custom API framework', 'Auth management', 'Auto-retry & fallback'],
  },
  {
    num: '02',
    icon: 'MapPinIcon',
    label: 'Multi-partner Live Tracking Aggregation',
    desc: 'All active deliveries across every connected partner appear on a single live map — one screen, real-time GPS, no toggling between partner dashboards. Dispatchers see every shipment regardless of which partner is fulfilling it.',
    pills: ['Single map view', 'Real-time GPS', 'All partners unified', 'Dispatcher dashboard'],
  },
  {
    num: '03',
    icon: 'CubeIcon',
    label: 'Unified Order & Fulfilment Management',
    desc: 'Send any order to any partner from one panel, track its full lifecycle in one place, and pull fulfilment history in one report. No more logging into seven partner portals or reconciling data across spreadsheets.',
    pills: ['One dispatch panel', 'Full lifecycle view', 'Unified history', 'Cross-partner search'],
  },
  {
    num: '04',
    icon: 'BoltIcon',
    label: 'Auto-routing to Best Available Partner',
    desc: "The routing engine evaluates every active partner in real time — serviceability, current load, historical SLA adherence, delivery cost, and order priority — and assigns the order to the optimal partner automatically. Manual override is always available.",
    pills: ['Real-time partner scoring', 'Cost + SLA matrix', 'Priority-aware routing', 'Manual override'],
  },
  {
    num: '05',
    icon: 'DocumentChartBarIcon',
    label: 'Cross-platform Delivery Analytics',
    desc: 'A single analytics layer aggregates data from all partners — on-time rates, average delivery time, cost per order, zone-level performance, and SLA breach frequency — so you can make data-driven decisions about which partners to use and where.',
    pills: ['Partner comparison', 'Zone-level breakdown', 'Cost per order', 'SLA breach reports'],
  },
  {
    num: '06',
    icon: 'ShieldCheckIcon',
    label: 'SLA Monitoring & Partner Performance',
    desc: 'Set SLA thresholds per partner and per delivery zone. The platform monitors every shipment against those benchmarks in real time — alerting your team when breaches occur and automatically scoring partners based on historical performance.',
    pills: ['SLA thresholds', 'Real-time breach alerts', 'Partner scoring', 'Historical benchmarks'],
  },
];

const withoutPains = [
  'Separate developer effort for each partner API',
  'Multiple partner dashboards open simultaneously',
  'Manual copy-paste of order data across systems',
  'No visibility into why a partner fails to deliver',
  'Adding a new partner takes weeks of engineering',
  'SLA breaches discovered days after they happen',
  'Costs compared via spreadsheets, not real time',
  'Customer complaints handled without delivery data',
];

const withBenefits = [
  'One integration covers all partners, past and future',
  'Single map shows all deliveries from all partners',
  'Orders dispatched to any partner in one click',
  'Partner failure triggers auto-fallback instantly',
  'New partner connected in days via pre-built adapter',
  'SLA breach alerts in real time, before escalation',
  'Live cost-per-order comparison across all partners',
  'Customer support has full delivery context always',
];

const routingCriteria = [
  { icon: 'MapPinIcon', label: 'Serviceability Check', desc: 'Is the partner active and accepting orders in this delivery zone right now?' },
  { icon: 'ClockIcon', label: 'ETA Comparison', desc: 'Which partner can pick up and deliver within the promised time window?' },
  { icon: 'CurrencyRupeeIcon', label: 'Cost Optimisation', desc: 'Which partner offers the lowest rate for this order size and distance?' },
  { icon: 'ChartBarIcon', label: 'SLA Track Record', desc: 'What is each partner\'s on-time success rate for this zone in the last 30 days?' },
  { icon: 'TruckIcon', label: 'Vehicle Matching', desc: 'Does the partner\'s available vehicle type match the order\'s dimensions and weight?' },
  { icon: 'BoltIcon', label: 'Priority Handling', desc: 'For express or high-priority orders, partners with guaranteed SLAs are ranked first.' },
];

const integrationMethods = [
  {
    icon: 'CodeBracketIcon',
    label: 'REST API',
    sub: 'Full integration',
    desc: 'Complete RESTful API with OAuth 2.0 authentication, webhook event subscriptions, and real-time status callbacks. Supports all major programming languages.',
    tags: ['OAuth 2.0', 'Webhooks', 'REST', 'Real-time'],
    color: '#0F766E',
  },
  {
    icon: 'BellAlertIcon',
    label: 'Webhooks',
    sub: 'Event-driven',
    desc: 'Subscribe to order lifecycle events — created, picked up, in-transit, delivered, failed. Your system receives instant push notifications at each stage.',
    tags: ['Push events', 'Order stages', 'Instant alerts', 'Configurable'],
    color: '#0E7490',
  },
  {
    icon: 'CubeIcon',
    label: 'SDK & Libraries',
    sub: 'Quick integration',
    desc: 'Official client libraries for Python, Node.js, Java, and PHP reduce integration time from weeks to hours with type-safe API wrappers and built-in retry logic.',
    tags: ['Python', 'Node.js', 'Java', 'PHP'],
    color: '#0891B2',
  },
  {
    icon: 'AdjustmentsHorizontalIcon',
    label: 'No-code Connector',
    sub: 'Dashboard-based',
    desc: 'Connect delivery partners through the admin dashboard without writing a single line of code. Credentials, routing rules, and SLA thresholds configured via UI.',
    tags: ['No engineering', 'UI-based', 'Point & click', 'Instant'],
    color: '#0F766E',
  },
];

const slaMetrics = [
  { icon: 'CheckCircleIcon', label: 'On-time Delivery Rate', sub: 'Per partner and per zone, tracked live' },
  { icon: 'ClockIcon', label: 'Average Delivery Duration', sub: 'From pickup to doorstep, by order type' },
  { icon: 'XCircleIcon', label: 'Failed & Returned Rate', sub: 'Identify partners with high exception rates' },
  { icon: 'CurrencyRupeeIcon', label: 'Cost per Delivery Trend', sub: 'Track cost efficiency over time per partner' },
  { icon: 'BoltIcon', label: 'Partner Availability Score', sub: 'Uptime and acceptance rate per zone' },
  { icon: 'StarIcon', label: 'Customer Satisfaction', sub: 'CSAT mapped per delivery partner' },
];

const additionalFeatures = [
  { icon: 'ArrowPathIcon', label: 'Auto-fallback on Partner Failure', sub: 'Instantly re-route if a partner rejects or fails' },
  { icon: 'DocumentTextIcon', label: 'Unified Invoice Reconciliation', sub: 'One reconciliation file for all partner billing' },
  { icon: 'MapPinIcon', label: 'Zone-based Partner Rules', sub: 'Set preferred partners per delivery zone or time' },
  { icon: 'ShieldCheckIcon', label: 'Proof of Delivery Aggregation', sub: 'Collect POD photos and signatures across all partners' },
  { icon: 'ChatBubbleLeftRightIcon', label: 'Customer Tracking Portal', sub: 'One branded tracking URL regardless of which partner ships' },
  { icon: 'BellAlertIcon', label: 'Breach Escalation Alerts', sub: 'Notify team leads when SLAs are at risk' },
];

const stats = [
  { value: 'All Partners', label: 'One Unified API Hub', icon: 'ArrowsRightLeftIcon' },
  { value: 'Real-time', label: 'Multi-partner Tracking', icon: 'MapPinIcon' },
  { value: 'AI-routed', label: 'Auto Best-partner Selection', icon: 'BoltIcon' },
  { value: 'Days', label: 'To Add a New Partner', icon: 'BoltIcon' },
];

/* ─── COMPONENT ─────────────────────────────────────────── */

export default function DeliveryIntegrationsContent() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (el) {
      const t = setTimeout(() => {
        el.querySelectorAll('.animate-on-scroll').forEach((item) => item.classList.add('is-visible'));
      }, 30);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="pt-20">

      {/* ── HERO (very dark teal + integration hub diagram) ── */}
      <div
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: '#042F2E' }}
      >
        <div className="absolute inset-0 circuit-bg opacity-30 pointer-events-none" />
        <div className="orb absolute w-[700px] h-[500px] rounded-full opacity-20 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(20,184,166,0.5) 0%, transparent 65%)', top: '-100px', right: '-150px' }} />
        <div className="orb-2 absolute w-[400px] h-[400px] rounded-full opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(15,118,110,0.7) 0%, transparent 70%)', bottom: '-80px', left: '-80px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div>
              <Link href="/products" className="animate-on-scroll stagger-1 inline-flex items-center gap-2 text-white/45 text-sm font-medium mb-7 hover:text-white/75 transition-colors">
                <Icon name="ArrowLeftIcon" size={14} />
                Back to Products
              </Link>

              <span className="animate-on-scroll stagger-1 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#5EEAD4]/35 bg-[#5EEAD4]/10 text-[11px] font-semibold tracking-widest text-[#5EEAD4] uppercase mb-5">
                <Icon name="ArrowsRightLeftIcon" size={11} />
                Delivery Integrations
              </span>

              <h1 className="animate-on-scroll stagger-2 font-display text-5xl md:text-6xl font-semibold text-white leading-tight tracking-tight mb-5 mt-2">
                Connect all delivery
                <br />
                <span className="italic font-light" style={{ color: '#5EEAD4' }}>partners. One API.</span>
                <br />
                <span style={{ color: '#14B8A6' }}>Zero custom builds.</span>
              </h1>

              <p className="animate-on-scroll stagger-3 text-white/55 text-lg leading-relaxed mb-9 max-w-lg">
                Extend your delivery reach by integrating third-party networks, logistics carriers, and hyperlocal couriers — through a single integration layer that handles routing, tracking, analytics, and SLA monitoring.
              </p>

              <div className="animate-on-scroll stagger-4 flex flex-wrap gap-4 mb-8">
                <Link href="/demo-booking" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-semibold text-[#042F2E] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5" style={{ background: 'linear-gradient(135deg, #5EEAD4, #14B8A6)' }}>
                  Book a Free Demo
                  <Icon name="ArrowRightIcon" size={16} />
                </Link>
                <Link href="/contact" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-sm font-medium text-white/70 border border-white/15 hover:border-white/35 hover:text-white transition-all">
                  <Icon name="PhoneIcon" size={15} />
                  +91 9600830488
                </Link>
              </div>

              <div className="animate-on-scroll stagger-5 flex flex-wrap gap-x-6 gap-y-2">
                {['One unified API hub', 'Add partners in days', 'Real-time all-partner tracking', 'AI auto-routing'].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 text-xs text-white/40 font-medium">
                    <Icon name="CheckCircleIcon" size={13} className="text-[#14B8A6]" />
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — integration hub diagram */}
            <div className="animate-on-scroll stagger-3">
              <div className="rounded-2xl overflow-hidden border border-white/10" style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)' }}>
                <div className="px-6 py-4 border-b border-white/[0.07]">
                  <p className="text-[11px] font-semibold text-white/30 uppercase tracking-widest">Integration Architecture</p>
                </div>
                <div className="p-6">
                  {/* Your platform */}
                  <div className="rounded-xl p-4 text-center border border-[#5EEAD4]/25 mb-4" style={{ background: 'rgba(20,184,166,0.15)' }}>
                    <div className="flex items-center justify-center gap-2">
                      <Icon name="ComputerDesktopIcon" size={16} className="text-[#5EEAD4]" />
                      <span className="text-sm font-bold text-[#5EEAD4]">Your Platform</span>
                    </div>
                    <p className="text-white/40 text-[10px] mt-1">eCommerce · Food delivery · Grocery · Any platform</p>
                  </div>

                  {/* Arrow + hub */}
                  <div className="flex items-center justify-center mb-4">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-0.5 h-4 bg-[#14B8A6]/40" />
                      <div className="w-0.5 h-4 bg-[#14B8A6]/40" />
                    </div>
                  </div>
                  <div className="rounded-xl p-4 text-center border border-[#14B8A6]/35 mb-4" style={{ background: 'rgba(15,118,110,0.25)' }}>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <Icon name="ArrowsRightLeftIcon" size={16} className="text-[#5EEAD4]" />
                      <span className="text-sm font-bold text-white">Delivery Integrations Hub</span>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 mt-2">
                      {['Auto-routing', 'Live tracking', 'Analytics', 'SLA monitor'].map((t) => (
                        <span key={t} className="text-[9px] px-2 py-0.5 rounded-full text-[#5EEAD4] font-semibold" style={{ background: 'rgba(20,184,166,0.15)' }}>{t}</span>
                      ))}
                    </div>
                  </div>

                  {/* Arrow + partners */}
                  <div className="flex items-center justify-center mb-4">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-0.5 h-4 bg-[#14B8A6]/40" />
                      <div className="w-0.5 h-4 bg-[#14B8A6]/40" />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-white/30 uppercase tracking-widest text-center mb-3">Connected Delivery Partners</p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { icon: 'TruckIcon', label: 'Last-mile', color: '#0F766E' },
                        { icon: 'BoltIcon', label: 'Hyperlocal', color: '#0891B2' },
                        { icon: 'BuildingStorefrontIcon', label: 'Logistics', color: '#0F766E' },
                        { icon: 'UserGroupIcon', label: 'Own Fleet', color: '#14B8A6' },
                        { icon: 'ArrowPathIcon', label: 'Aggregators', color: '#0891B2' },
                        { icon: 'PlusCircleIcon', label: 'Add More', color: '#0F766E' },
                      ].map((p) => (
                        <div key={p.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-white/[0.07] bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: p.color }}>
                            <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={13} className="text-white" />
                          </div>
                          <span className="text-[9px] font-semibold text-white/60">{p.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── METRICS STRIP ── */}
      <div style={{ background: 'linear-gradient(135deg, #042F2E 0%, #0F766E 55%, #14B8A6 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {stats.map((s, i) => (
              <div key={s.label} className={`animate-on-scroll stagger-${i + 1} flex items-center gap-4 px-6 py-7`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/15">
                  <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#5EEAD4]" />
                </div>
                <div>
                  <div className="font-display text-2xl font-semibold text-white leading-none mb-0.5">{s.value}</div>
                  <div className="text-xs text-white/55 font-medium">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WITHOUT vs WITH (unique comparison) ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-4">
              The Integration Challenge
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Before and after{' '}
              <span className="italic font-light text-[#0F766E]">Delivery Integrations.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Managing multiple delivery partners without a unified layer is a constant operational drag — here's what changes.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Without */}
            <div className="animate-on-scroll stagger-2 rounded-2xl p-8 border border-[rgba(239,68,68,0.15)] bg-[#FEF2F2]">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#FEE2E2]">
                  <Icon name="XCircleIcon" size={20} className="text-[#DC2626]" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A]">Without Delivery Integrations</h3>
              </div>
              <div className="flex flex-col gap-3">
                {withoutPains.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-[#FEE2E2]">
                      <Icon name="XMarkIcon" size={11} className="text-[#DC2626]" />
                    </div>
                    <p className="text-sm text-[#475569] leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* With */}
            <div className="animate-on-scroll stagger-3 rounded-2xl p-8 border border-[rgba(15,118,110,0.2)]" style={{ background: 'linear-gradient(135deg, rgba(15,118,110,0.05), rgba(20,184,166,0.02))' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F766E, #0E7490)' }}>
                  <Icon name="CheckCircleIcon" size={20} className="text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0F172A]">With Delivery Integrations</h3>
              </div>
              <div className="flex flex-col gap-3">
                {withBenefits.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'linear-gradient(135deg, #0F766E, #0E7490)' }}>
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <p className="text-sm text-[#475569] leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-[rgba(15,118,110,0.15)]">
                <p className="font-semibold text-[#0F766E] text-sm">One API. Every partner. Total control.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CORE 6 FEATURES ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-4">
              Key Features
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Six capabilities that unify{' '}
              <span className="italic font-light text-[#0F766E]">your entire delivery network.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {coreFeatures.map((f, i) => (
              <div
                key={f.num}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl overflow-hidden border hover:shadow-lg transition-all duration-300`}
                style={{ borderColor: 'rgba(15,118,110,0.15)', background: i % 2 === 0 ? 'white' : '#F0FDFA' }}
              >
                {/* Top accent line */}
                <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #0F766E, #14B8A6)' }} />
                <div className="p-7">
                  <div className="flex items-start gap-4 mb-4">
                    <span
                      className="font-mono-custom text-[11px] font-bold w-8 h-8 rounded-full flex items-center justify-center text-white flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #0F766E, #0E7490)' }}
                    >
                      {f.num}
                    </span>
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{ background: 'rgba(15,118,110,0.1)' }}
                    >
                      <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0F766E]" />
                    </div>
                    <h3 className="font-display text-base font-semibold text-[#0F172A] leading-snug pt-1 flex-1">{f.label}</h3>
                  </div>
                  <p className="text-sm text-[#475569] leading-relaxed mb-4">{f.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {f.pills.map((p) => (
                      <span key={p} className="text-[11px] font-semibold px-2.5 py-1 rounded-full text-[#0F766E]" style={{ background: 'rgba(15,118,110,0.08)' }}>
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PARTNER ECOSYSTEM ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-4">
              Partner Ecosystem
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Every type of delivery partner,{' '}
              <span className="italic font-light text-[#0F766E]">connected.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Whether you need same-hour hyperlocal delivery or intercity freight, the integration layer connects to all partner categories.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {partnerTypes.map((p, i) => (
              <div
                key={p.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-6 border border-[rgba(15,118,110,0.12)] hover:border-[rgba(15,118,110,0.3)] hover:shadow-md transition-all duration-300`}
                style={{ background: i % 2 === 0 ? '#F0FDFA' : 'white' }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(15,118,110,0.1)' }}>
                  <Icon name={p.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0F766E]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">{p.label}</h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-3">{p.desc}</p>
                <p className="text-[11px] font-semibold text-[#0F766E]">{p.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── AUTO-ROUTING ENGINE (dark) ── */}
      <div className="py-24 relative overflow-hidden" style={{ background: '#042F2E' }}>
        <div className="absolute inset-0 circuit-bg opacity-25 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(15,118,110,0.45) 0%, transparent 70%)', filter: 'blur(50px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#5EEAD4]/30 bg-[#5EEAD4]/10 text-[11px] font-semibold tracking-widest text-[#5EEAD4] uppercase mb-5">
              Auto-routing Intelligence
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">
              Every order assigned to the{' '}
              <span className="italic font-light text-[#5EEAD4]">best partner, instantly.</span>
            </h2>
            <p className="text-white/50 text-lg mt-4 max-w-xl mx-auto">Six criteria evaluated in real time before every dispatch decision — no manual work, no guesswork.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {routingCriteria.map((r, i) => (
              <div
                key={r.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} group rounded-2xl p-6 border border-white/[0.08] hover:border-[#5EEAD4]/30 hover:bg-white/[0.04] transition-all duration-300 bg-white/[0.03]`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(20,184,166,0.15)', border: '1px solid rgba(94,234,212,0.2)' }}>
                    <Icon name={r.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#5EEAD4]" />
                  </div>
                  <h3 className="font-semibold text-white text-sm">{r.label}</h3>
                </div>
                <p className="text-sm text-white/50 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 mt-12 flex flex-wrap justify-center gap-3">
            {['Decision in milliseconds', 'Manual override always available', 'AI improves with every order', 'Zone-level partner preferences configurable'].map((p) => (
              <div key={p} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#5EEAD4]/20 bg-white/[0.04]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#5EEAD4]" />
                <span className="text-sm text-white/55 font-medium">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── INTEGRATION METHODS ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-4">
              Integration Methods
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Connect the way{' '}
              <span className="italic font-light text-[#0F766E]">your team works best.</span>
            </h2>
            <p className="text-[#475569] text-lg mt-4 max-w-xl mx-auto">Whether you have a full engineering team or prefer no-code setup, there is an integration path built for your capability.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {integrationMethods.map((m, i) => (
              <div
                key={m.label}
                className={`animate-on-scroll stagger-${i + 2} group rounded-2xl overflow-hidden border border-[rgba(15,118,110,0.12)] hover:border-[rgba(15,118,110,0.3)] hover:shadow-md transition-all duration-300`}
              >
                <div className="h-1" style={{ background: `linear-gradient(90deg, ${m.color}, #14B8A6)` }} />
                <div className="p-6 bg-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform" style={{ background: 'rgba(15,118,110,0.1)' }}>
                      <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0F766E]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0F172A] text-sm">{m.label}</p>
                      <p className="text-[10px] text-[#64748B]">{m.sub}</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#475569] leading-relaxed mb-4">{m.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {m.tags.map((t) => (
                      <span key={t} className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-[#0F766E]" style={{ background: 'rgba(15,118,110,0.08)' }}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SLA MONITORING ── */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-5">
                SLA Monitoring
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-5">
                Know exactly how every{' '}
                <span className="italic font-light text-[#0F766E]">partner is performing.</span>
              </h2>
              <p className="text-[#475569] text-lg leading-relaxed mb-8">
                Set SLA thresholds per partner, per zone, and per order type. The platform monitors every shipment against those benchmarks in real time — alerting your team the moment a breach is approaching, not after it happens.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  'Set breach thresholds at partner and zone level',
                  'Real-time alerts before SLA is breached, not after',
                  'Automatic partner score recalculation after each breach',
                  'Low-performing partners deprioritised in auto-routing',
                  'Weekly and monthly SLA reports per partner',
                  'Exportable data for vendor contract reviews',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0F766E, #0E7490)' }}>
                      <Icon name="CheckIcon" size={11} className="text-white" />
                    </div>
                    <span className="text-sm text-[#475569]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="animate-on-scroll stagger-2 grid grid-cols-2 gap-4">
              {slaMetrics.map((m, i) => (
                <div
                  key={m.label}
                  className={`animate-on-scroll stagger-${i + 2} rounded-2xl p-5 border border-[rgba(15,118,110,0.1)] hover:border-[rgba(15,118,110,0.3)] hover:shadow-md transition-all group`}
                  style={{ background: i % 2 === 0 ? '#F0FDFA' : 'white' }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ background: 'rgba(15,118,110,0.1)' }}>
                    <Icon name={m.icon as Parameters<typeof Icon>[0]['name']} size={18} className="text-[#0F766E]" />
                  </div>
                  <p className="font-semibold text-sm text-[#0F172A] mb-1">{m.label}</p>
                  <p className="text-xs text-[#64748B] leading-relaxed">{m.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── ADDITIONAL FEATURES ── */}
      <div className="py-24" style={{ background: '#F0FDFA' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[rgba(15,118,110,0.3)] bg-[rgba(15,118,110,0.07)] text-[11px] font-semibold tracking-widest text-[#0F766E] uppercase mb-4">
              More Capabilities
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              The complete delivery{' '}
              <span className="italic font-light text-[#0F766E]">integration layer.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
            {additionalFeatures.map((f, i) => (
              <div
                key={f.label}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} flex items-center gap-4 p-6 rounded-2xl border border-[rgba(15,118,110,0.1)] bg-white hover:border-[rgba(15,118,110,0.3)] hover:shadow-md transition-all duration-300 group`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ background: 'rgba(15,118,110,0.1)' }}>
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0F766E]" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-[#0F172A]">{f.label}</p>
                  <p className="text-xs text-[#64748B] mt-0.5">{f.sub}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="animate-on-scroll stagger-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {['REST API + Webhooks', 'Real-time SLA Alerts', 'AI Auto-routing', 'Unified Analytics'].map((item) => (
              <div key={item} className="flex items-center gap-2 p-3 bg-white rounded-xl border border-[rgba(15,118,110,0.12)]">
                <Icon name="CheckBadgeIcon" size={16} className="text-[#0F766E] flex-shrink-0" />
                <span className="text-xs font-medium text-[#475569]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <div className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="animate-on-scroll stagger-1 rounded-3xl p-12 md:p-16 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #042F2E 0%, #0F766E 50%, #14B8A6 100%)' }}
          >
            <div className="absolute inset-0 circuit-bg opacity-25 pointer-events-none" />
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.15) 0%, transparent 70%)', transform: 'translate(50px,-50px)' }} />

            <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#5EEAD4]/30 bg-[#5EEAD4]/10 text-[11px] font-semibold tracking-widest text-[#5EEAD4] uppercase mb-6">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-[#5EEAD4] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#5EEAD4]" />
              </span>
              Ready to Connect?
            </span>

            <h2 className="relative font-display text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
              Extend your delivery reach{' '}
              <span className="italic font-light text-[#5EEAD4]">in days, not months.</span>
            </h2>

            <p className="relative text-white/60 text-lg mb-5 max-w-xl mx-auto leading-relaxed">
              For demo or more information
            </p>

            <div className="relative flex flex-wrap justify-center gap-x-5 gap-y-2 mb-10 text-sm">
              <a href="tel:+919600830488" className="inline-flex items-center gap-2 text-white/60 font-medium hover:text-white transition-colors">
                <Icon name="PhoneIcon" size={14} />
                +91 9600830488
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <a href="mailto:contact@movio.in" className="inline-flex items-center gap-2 text-white/60 font-medium hover:text-white transition-colors">
                <Icon name="EnvelopeIcon" size={14} />
                contact@movio.in
              </a>
              <span className="text-white/25 hidden sm:inline">|</span>
              <span className="inline-flex items-center gap-2 text-white/60 font-medium">
                <Icon name="GlobeAltIcon" size={14} />
                www.movio.in
              </span>
            </div>

            <div className="relative flex flex-wrap justify-center gap-4">
              <Link
                href="/demo-booking"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-semibold text-[#042F2E] hover:opacity-90 shadow-lg transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #5EEAD4, #14B8A6)' }}
              >
                Book Free Demo
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-xl text-base font-medium text-white border-2 border-white/30 hover:border-white/60 hover:bg-white/10 transition-all duration-200"
              >
                <Icon name="PhoneIcon" size={16} />
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
